/**
 * Approvals — pending queue + approve/reject actions for maker-checker
 * sensitive operations. The schema and ApprovalsService back this; the
 * approver action then dispatches to the underlying service (dealer
 * suspend, loan/device write-off, etc.) under the approver's user id.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError, type Approval, type ApprovalStatus } from "@dfp/api-client";
import { Badge, type BadgeTone } from "../components/Badge.js";
import { Button } from "../components/Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { Table, TBody, THead, Th, Td, Tr } from "../components/Table.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";

const STATUS_TONE: Record<ApprovalStatus, BadgeTone> = {
  pending: "warning",
  approved: "brand",
  rejected: "danger",
  executed: "success",
  expired: "neutral",
};

export function ApprovalsPage(): JSX.Element {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [activeStatus, setActiveStatus] = useState<ApprovalStatus | "all">("pending");
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const perms = auth.user?.permissions ?? [];
  const canApprove = perms.includes("iam.approval.approve");
  const canReject = perms.includes("iam.approval.reject");

  const list = useQuery({
    queryKey: ["approvals", "list", activeStatus],
    queryFn: () =>
      api.get<readonly Approval[]>(
        activeStatus === "all" ? "/api/approvals" : `/api/approvals?status=${activeStatus}`,
      ),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["approvals", "list"] });
  };

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.post<Approval>(`/api/approvals/${id}/approve`, {}),
    onSuccess: () => {
      invalidate();
      setError(null);
    },
    onError: (err: unknown) => setError(formatError(err)),
  });

  const rejectMutation = useMutation({
    mutationFn: (args: { id: string; reason: string }) =>
      api.post<Approval>(`/api/approvals/${args.id}/reject`, { reason: args.reason }),
    onSuccess: () => {
      invalidate();
      setRejectingId(null);
      setRejectReason("");
      setError(null);
    },
    onError: (err: unknown) => setError(formatError(err)),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Approvals</h1>
        <p className="text-sm text-slate-500">
          Maker-checker workflow for sensitive actions. Approvers cannot approve their own requests.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {(["pending", "approved", "executed", "rejected", "all"] as const).map((s) => (
          <Button
            key={s}
            variant={activeStatus === s ? "primary" : "secondary"}
            onClick={() => setActiveStatus(s)}
          >
            {s}
          </Button>
        ))}
      </div>

      {error ? (
        <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>
            {activeStatus === "all" ? "All approvals" : `${activeStatus} approvals`}
          </CardTitle>
          <span className="text-xs text-slate-500">{list.data?.length ?? 0} rows</span>
        </CardHeader>
        <CardBody className="p-0">
          {list.isLoading ? (
            <p className="p-5 text-sm text-slate-500">Loading…</p>
          ) : list.isError ? (
            <p className="p-5 text-sm text-red-700">Failed to load approvals.</p>
          ) : (list.data ?? []).length === 0 ? (
            <p className="p-5 text-sm text-slate-500">No approvals in this view.</p>
          ) : (
            <Table>
              <THead>
                <tr>
                  <Th>When</Th>
                  <Th>Permission</Th>
                  <Th>Requested by</Th>
                  <Th>Reason</Th>
                  <Th>Payload</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </tr>
              </THead>
              <TBody>
                {list.data?.map((a) => (
                  <Tr key={a.id}>
                    <Td className="text-xs text-slate-500 whitespace-nowrap">
                      {new Date(a.createdAt).toLocaleString()}
                    </Td>
                    <Td>
                      <code className="text-xs bg-slate-100 px-1 rounded">{a.permissionKey}</code>
                    </Td>
                    <Td className="font-mono text-xs">{a.requestedBy.slice(0, 8)}…</Td>
                    <Td className="text-xs text-slate-700 max-w-xs truncate">
                      <span title={a.reason}>{a.reason}</span>
                    </Td>
                    <Td>
                      <details>
                        <summary className="cursor-pointer text-xs text-brand-600">view</summary>
                        <pre className="mt-1 max-w-md overflow-auto rounded bg-slate-50 border border-slate-200 p-2 text-xs">
                          {JSON.stringify(a.payload, null, 2)}
                        </pre>
                      </details>
                    </Td>
                    <Td>
                      <Badge tone={STATUS_TONE[a.status]}>{a.status}</Badge>
                      {a.rejectionReason ? (
                        <p className="mt-1 text-xs text-red-700">
                          <span title={a.rejectionReason}>{a.rejectionReason.slice(0, 32)}…</span>
                        </p>
                      ) : null}
                    </Td>
                    <Td>
                      {a.status === "pending" ? (
                        <div className="flex flex-wrap gap-1">
                          {canApprove && a.requestedBy !== auth.user?.sub ? (
                            <Button
                              onClick={() => approveMutation.mutate(a.id)}
                              disabled={approveMutation.isPending}
                            >
                              Approve
                            </Button>
                          ) : null}
                          {canReject && a.requestedBy !== auth.user?.sub ? (
                            <Button
                              variant="danger"
                              onClick={() => {
                                setRejectingId(a.id);
                                setRejectReason("");
                              }}
                              disabled={rejectMutation.isPending}
                            >
                              Reject
                            </Button>
                          ) : null}
                          {a.requestedBy === auth.user?.sub ? (
                            <span className="text-xs text-slate-500">your request</span>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>

      {rejectingId ? (
        <Card>
          <CardHeader>
            <CardTitle>Reject approval</CardTitle>
          </CardHeader>
          <CardBody className="space-y-2">
            <label className="block">
              <span className="text-xs font-medium text-slate-700">
                Rejection reason (≥ 10 chars)
              </span>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </label>
            <div className="flex gap-2">
              <Button
                variant="danger"
                onClick={() =>
                  rejectMutation.mutate({ id: rejectingId, reason: rejectReason.trim() })
                }
                disabled={rejectMutation.isPending || rejectReason.trim().length < 10}
              >
                Confirm rejection
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setRejectingId(null);
                  setRejectReason("");
                }}
              >
                Cancel
              </Button>
            </div>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) {
    return err.problem.message ?? err.problem.detail ?? "Request rejected";
  }
  return "Network error";
}
